// Personalized weekly cheat sheet / revision sheet / formula sheet — one per
// ISO week (src/jobs/notificationBatch.ts's processWeeklySheets, Mondays
// 06:00 IST), generated from the student's own last 7 days of struggle
// (missed exam + practice attempts): formulas/tips, common errors, and a
// worked example per weak sub-topic. Rendered in-app for V1 (no PDF/S3, like
// the PTM report gets — a natural follow-up if wanted).
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/lib/api';
import { decodeClaims } from '@/lib/auth';
import { studentId } from '@/hooks/useTaskCards';
import type { WeeklySheetDTO } from '@/types';
import { Card, StudentPage, StudentPageHeader } from '@/components/ds';

export function WeeklySheetPage() {
  const claims = useMemo(() => decodeClaims(), []);
  const { weekKey } = useParams();
  const sid = studentId();

  const sheetQuery = useQuery({
    queryKey: ['weekly-sheet', sid, weekKey],
    enabled: Boolean(sid),
    queryFn: () =>
      apiGet<WeeklySheetDTO | null>(
        `/dashboard/${sid}/weekly-sheet${weekKey ? `?weekKey=${weekKey}` : ''}`,
      ),
  });

  const sheet = sheetQuery.data;

  return (
    <StudentPage className="gap-4">
      <StudentPageHeader
        breadcrumbs={
          weekKey
            ? [{ label: 'Weekly sheet', to: '/practice/weekly-sheet' }, { label: weekKey }]
            : undefined
        }
        title="This week's revision sheet"
        subtitle={`${claims ? `Grade ${claims.grade === 'TEN' ? '10' : '12'} · ${claims.board}` : ''} · Formulas, common errors & a worked example for what you've struggled with this week.`}
      />

      {sheetQuery.isLoading ? (
        <div className="flex flex-col gap-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-32 animate-pulse rounded-2xl bg-paper-sunk" />
          ))}
        </div>
      ) : !sheet || sheet.sections.length === 0 ? (
        <Card>
          <p className="text-sm text-ink-soft">
            No sheet yet — either it's early in the week, or you haven't struggled with anything
            recently (nice!). Check back after your next few attempts.
          </p>
        </Card>
      ) : (
        <div className="flex flex-col gap-3">
          {sheet.sections.map((s) => (
            <Card key={s.subtopicId} title={s.subtopicName}>
              <div className="flex flex-col gap-3 text-sm">
                {s.formulas.length > 0 && (
                  <div>
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-ink-faint">
                      Formulas &amp; tips
                    </p>
                    <ul className="flex flex-col gap-1">
                      {s.formulas.map((f, i) => (
                        <li key={i} className="rounded-lg bg-paper-sunk px-3 py-1.5 text-ink">
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {s.commonErrors.length > 0 && (
                  <div>
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-ink-faint">
                      Common errors to avoid
                    </p>
                    <ul className="flex flex-col gap-1">
                      {s.commonErrors.map((e, i) => (
                        <li
                          key={i}
                          className="rounded-lg px-3 py-1.5"
                          style={{
                            background: 'var(--orange-bg)',
                            color: 'var(--orange-text, #ef6c00)',
                          }}
                        >
                          {e}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {s.workedExample && (
                  <div>
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-ink-faint">
                      Worked example
                    </p>
                    <p
                      className="rounded-lg px-3 py-1.5"
                      style={{ background: 'var(--green-bg)', color: 'var(--green-text)' }}
                    >
                      {s.workedExample}
                    </p>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </StudentPage>
  );
}
